import { requestAvatarSelection } from "../utils/avatar-selection-events";
import {
  idForAvatarUrl,
  isLocalHubsUrl,
  isLocalHubsSceneUrl,
  isHubsRoomUrl,
  isLocalOrPersistenceAvatarUrl
} from "../utils/media-url-utils";
import { guessContentType } from "../utils/media-url-utils";
import { handleExitTo2DInterstitial } from "../utils/vr-interstitial";
import { changeHub, sendChangeHubMessage } from "../change-hub";
import { ObjectContentOrigins } from "../object-types";
import { addMedia } from "../utils/media-utils";

AFRAME.registerComponent("open-media-button", {
  schema: {
    onlyOpenLink: { type: "boolean", default: false },
    branded: { type: "boolean", default: false },
    invisible: { type: "boolean", default: false }
  },
  spawnMediaInFrontOfPlayerAndReturn(src, contentOrigin, mediaOptions) {
    const offset = { x: 0, y: 0, z: -1.5 };
    const { entity, orientation } = addMedia(
      src,
      "#interactable-media",
      contentOrigin,
      src.name === "message.png" ? "message" : null,
      !(src instanceof MediaStream),
      true,
      true,
      mediaOptions
    );
    orientation.then(or => {
      entity.setAttribute("offset-relative-to", {
        target: "#avatar-pov-node",
        offset,
        orientation: or
      });
    });

    return entity;
  },
  init() {
    this.label = this.el.querySelector("[text]");

    this.updateSrc = async () => {
      if (!this.targetEl.parentNode) return; // If removed
      const mediaLoader = this.targetEl.components["media-loader"].data;
      const src = (this.src = (mediaLoader.mediaOptions && mediaLoader.mediaOptions.href) || mediaLoader.src);
      const visible = src && guessContentType(src) !== "video/vnd.hubs-webrtc";
      const mayChangeScene = this.el.sceneEl.systems.permissions.canOrWillIfCreator("update_hub");
      const sprite = this.el.querySelector("a-entity[icon-button]");

      this.el.object3D.visible = !!visible;

      if (visible) {
        let label = "Open Link";
        if (!this.data.onlyOpenLink) {
          let hubId;
          if (await isLocalOrPersistenceAvatarUrl(src)) {
            label = "Use Avatar";
          } else if ((await isLocalHubsSceneUrl(src)) && mayChangeScene) {
            label = "Enter";
            sprite.setAttribute("icon-button", "active", true);
          } else if ((hubId = await isHubsRoomUrl(src))) {
            const url = new URL(src);
            if (url.hash && window.APP.hub.hub_id === hubId) {
              label = "Go To";
            } else {
              label = "Open Verse";
            }
            sprite.setAttribute("icon-button", "active", true);
          }
        }
        this.label.setAttribute("text", "value", label);
      }
    };

    this.onClick = async () => {
      const sceneEl = this.el.sceneEl;
      const mayChangeScene = this.el.sceneEl.systems.permissions.canOrWillIfCreator("update_hub");

      const exitImmersive = async () => await handleExitTo2DInterstitial(false, () => {}, true);
      const openAsIframe = this.targetEl?.components["media-loader"]?.data?.mediaOptions?.isIframeLink;

      const openNewTab = async () => {
        await exitImmersive();

        const handle = window.open(this.src);

        if (!handle) {
          this.el.sceneEl.emit("open_link_failed", { url: this.src });
        }
      };

      const mediaLoader = this.targetEl.components["media-loader"].data;
      const smoothTransition = mediaLoader.mediaOptions?.smoothTransition ?? false;

      let hubId;
      const openVRPdfElement = async () => {
        const contentOrigin = this.src instanceof File ? ObjectContentOrigins.FILE : ObjectContentOrigins.URL;
        const mediaOptions = {};

        mediaOptions.href = this.src;
        mediaOptions.hideHref = true;
        mediaOptions.smoothTransition = false;
        mediaOptions.showOnlyMe = true;

        await this.spawnMediaInFrontOfPlayerAndReturn(this.src, contentOrigin, mediaOptions);
      };

      const isVR = sceneEl.is("vr-mode");

      if (openAsIframe) {
        if (isVR) {
          openVRPdfElement();
        } else {
          window.dispatchEvent(new CustomEvent("open-2D-iframe", { detail: this.src }));
        }
      } else if (this.data.onlyOpenLink) {
        console.log("Opening new tab");
        openNewTab();
      } else if (await isLocalOrPersistenceAvatarUrl(this.src)) {
        const avatarId = idForAvatarUrl(this.src);
        if (avatarId) {
          requestAvatarSelection(this.el.sceneEl, avatarId);
        }
      } else if ((await isLocalHubsSceneUrl(this.src)) && mayChangeScene) {
        window.smoothTransition = smoothTransition;
        APP.hubChannel.sendMessage({ smoothTransition }, "change_scene");
        this.el.sceneEl.emit("scene_media_selected", this.src);
      } else if ((hubId = await isHubsRoomUrl(this.src))) {
        const url = new URL(this.src);
        if (url.hash && window.APP.hub.hub_id === hubId) {
          // move to waypoint w/o writing to history
          window.history.replaceState(null, null, window.location.href.split("#")[0] + url.hash);
        } else if (isLocalHubsUrl(this.src)) {
          const waypoint = url.hash && url.hash.substring(1);
          // move to new room without page load or entry flow
          if (window.APP.hubChannel.canOrWillIfCreator("update_hub") && window.APP.hub.user_data?.enable_tour_portals) {
            sendChangeHubMessage(hubId, true, waypoint, smoothTransition);
          } else {
            changeHub(hubId, true, waypoint, smoothTransition);
          }
        } else {
          await exitImmersive();
          location.href = this.src;
        }
      } else {
        openNewTab();
      }
    };

    const interactableId = this.el.closest("[data-interactable-id]")?.dataset.interactableId;
    const interactableEl = interactableId ? document.getElementById(interactableId) : null;

    NAF.utils.getNetworkedEntity(interactableEl || this.el).then(networkedEl => {
      this.targetEl = networkedEl;
      this.targetEl.addEventListener("media_resolved", this.updateSrc, { once: true });
      if (this.data.invisible) {
        this.targetEl.object3D.addEventListener("interact", this.onClick);
      }
      this.updateSrc();
    });
  },
  play() {
    this.el.object3D.addEventListener("interact", this.onClick);
  },
  pause() {
    this.el.object3D.removeEventListener("interact", this.onClick);
  },
  remove() {
    if (this.targetEl && this.data.invisible) {
      this.targetEl.object3D.removeEventListener("interact", this.onClick);
    }
  }
});
